import {useState, useRef, useEffect, useCallback} from 'react';
import {useShadowRoot} from '../../../contexts/ShadowRootContext';
import '../../Tooltip/Tooltip.css';

/**
 * Native dropdown component that works properly within Shadow DOM.
 * Uses native HTML/CSS instead of react-bootstrap to avoid event handling issues.
 */
const HspDropdown = ({
  label,
  dropdownMenuItems,
  dropdownToggleItems,
  topLevelClassNames = '',
  includeSwatch = false
}) => {
  const {dropdownClasses, dropdownText} = dropdownToggleItems;
  const [show, setShow] = useState(false);
  const dropdownRef = useRef(null);
  const shadowRoot = useShadowRoot();

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setShow(prev => !prev);
  }, []);

  const handleItemClick = useCallback(() => {
    // Close dropdown after item click
    setShow(false);
  }, []);

  // Click-outside detection for Shadow DOM
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    // Use capture phase and delayed attachment to avoid catching opening click
    const timeoutId = setTimeout(() => {
      shadowRoot.addEventListener?.('click', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
    }, 10);
    
    return () => {
      clearTimeout(timeoutId);
      shadowRoot.removeEventListener?.('click', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [show, shadowRoot]);

  return (
    <div className={`hs-form-control-group ${topLevelClassNames}`} ref={dropdownRef}>
      <label
        htmlFor={label.labelKey}
        className={`form-label hs-form-label ${label.labelClasses ?? ''}`}>
        {label.labelContent}
        <strong>Incompatible</strong>
      </label>
      <div className="dropdown hs-form-dropdown" data-testid="dropdown">
        <button
          type="button"
          data-testid="dropdown-toggle"
          onClick={handleToggle}
          className={`btn uix-btn-outline-default btn-sm input-radius dropdown-toggle hs-form-dropdown-toggle ${dropdownClasses ?? ''} ${show ? 'show' : ''}`}
          title={label.labelKey}
          aria-expanded={show}
          aria-haspopup="true">
          {includeSwatch ? (
            <span className={`badge hs-form-badge bg-${dropdownText?.toLowerCase()}`}></span>
          ) : null}
          {dropdownText}
        </button>
        <div
          data-testid="dropdown-menu"
          className={`dropdown-menu hs-form-dropdown-menu ${show ? 'show' : ''}`}
          style={{margin: 0, display: show ? 'block' : 'none'}}
          aria-labelledby={label.labelKey}
          onClick={handleItemClick}>
          {dropdownMenuItems.map((item) => item)}
        </div>
      </div>
    </div>
  );
};

export default HspDropdown;
